/**
 * @file usePlatformResource.ts
 * @description Загрузка данных экрана кабинета платформы по ключу запроса
 * (phase19.28 этап А; образец — useConsentTextsOverview, ревью М1).
 *
 * Ответ хранится вместе с ключом запроса: «загрузка» — пока нет ответа на
 * текущий ключ; ответ на устаревший ключ не перетирает новый. Сбой повтора
 * не прячет прежние данные (полоса ошибки над старым списком). После
 * мутаций экран вызывает `reload` и перечитывает с сервера, а не правит
 * данные руками (один источник правды).
 */

import { useCallback, useEffect, useState } from "react";

import { ApiError } from "../../lib/api-client";

export interface PlatformResource<T> {
  data: T | null;
  loading: boolean;
  /** Текст ошибки для человека или null. */
  error: string | null;
  /** Код отказа API (`404`, `rbac_*`) — экрану, которому важна причина. */
  errorStatus: number | null;
  reload: () => void;
}

interface Loaded<T> {
  key: string;
  data: T | null;
  error: string | null;
  status: number | null;
}

/**
 * @param key  ключ запроса (адрес + параметры); `null` — не загружать.
 * @param load загрузчик для ключа.
 * @param errorText текст ошибки загрузки (§7 макета).
 */
export function usePlatformResource<T>(
  key: string | null,
  load: () => Promise<T>,
  errorText: string,
): PlatformResource<T> {
  const [nonce, setNonce] = useState(0);
  const [loaded, setLoaded] = useState<Loaded<T> | null>(null);
  const fullKey = key === null ? null : `${key}#${nonce}`;

  useEffect(() => {
    if (fullKey === null) return;
    let alive = true;
    load()
      .then((data) => ({ data, error: null, status: null }))
      .catch((err: unknown) => ({
        data: null,
        error: errorText,
        status: err instanceof ApiError ? err.status : null,
      }))
      .then((result) => {
        if (!alive) return;
        setLoaded((prev) => ({
          key: fullKey,
          data: result.data ?? (result.error ? (prev?.data ?? null) : null),
          error: result.error,
          status: result.status,
        }));
      });
    return () => {
      alive = false;
    };
    // load и errorText — по ключу: новый ключ = новый запрос.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullKey]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const current = loaded?.key === fullKey;
  return {
    data: loaded?.data ?? null,
    loading: fullKey !== null && !current,
    error: current ? (loaded?.error ?? null) : null,
    errorStatus: current ? (loaded?.status ?? null) : null,
    reload,
  };
}
